import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Plus, Edit2, Trash2, Save, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import Loader from '../../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statuses = [
  { value: 'investigating', label: 'Investigation', icon: AlertCircle, color: 'text-yellow-400' },
  { value: 'ongoing', label: 'En cours', icon: Clock, color: 'text-red-400' },
  { value: 'resolved', label: 'Résolu', icon: CheckCircle, color: 'text-green-400' }
];

const severities = [
  { value: 'minor', label: 'Mineur', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'major', label: 'Majeur', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'critical', label: 'Critique', color: 'bg-red-500/20 text-red-400' }
];

const AdminIncidents = () => {
  const { token } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'investigating',
    severity: 'minor'
  });

  const fetchIncidents = async () => {
    try {
      const response = await axios.get(`${API}/incidents?limit=50`);
      setIncidents(response.data);
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      status: 'investigating',
      severity: 'minor'
    });
    setEditingIncident(null);
  };

  const openDialog = (incident = null) => {
    if (incident) {
      setEditingIncident(incident);
      setFormData({
        title: incident.title,
        description: incident.description,
        status: incident.status,
        severity: incident.severity
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingIncident) {
        await axios.put(`${API}/admin/incidents/${editingIncident.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Incident modifié avec succès !');
      } else {
        await axios.post(`${API}/admin/incidents`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Incident créé avec succès !');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchIncidents();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
      console.error(error);
    }
  };

  const handleDelete = async (incidentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet incident ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/incidents/${incidentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Incident supprimé !');
      fetchIncidents();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
      console.error(error);
    }
  };

  const getStatusInfo = (status) => statuses.find(s => s.value === status) || statuses[0];
  const getSeverityInfo = (severity) => severities.find(s => s.value === severity) || severities[0];

  if (loading) {
    return <Loader text="Chargement des incidents..." />;
  }

  const activeIncidents = incidents.filter(i => i.status !== 'resolved');
  const resolvedIncidents = incidents.filter(i => i.status === 'resolved');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-yellow-400" />
            Gestion des Incidents
          </h1>
          <p className="text-gray-400">
            {activeIncidents.length} incident(s) actif(s) • {resolvedIncidents.length} résolu(s)
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => openDialog()}
              className="bg-violet-600 hover:bg-violet-500"
              data-testid="add-incident-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Signaler
            </Button>
          </DialogTrigger>
          
          <DialogContent className="bg-[#0a0a0a] border-white/10 text-white max-w-xl">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase tracking-wider">
                {editingIncident ? 'Modifier l\'incident' : 'Signaler un incident'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-gray-300">Titre</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Titre de l'incident"
                  required
                  className="bg-black/50 border-white/10 text-white mt-1"
                  data-testid="incident-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Statut</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1" data-testid="incident-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                      {statuses.map((status) => (
                        <SelectItem key={status.value} value={status.value} className="text-white">
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-gray-300">Sévérité</Label>
                  <Select 
                    value={formData.severity} 
                    onValueChange={(value) => setFormData({ ...formData, severity: value })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 text-white mt-1" data-testid="incident-severity-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-white/10">
                      {severities.map((sev) => (
                        <SelectItem key={sev.value} value={sev.value} className="text-white">
                          {sev.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Détails de l'incident..."
                  required
                  rows={4}
                  className="bg-black/50 border-white/10 text-white mt-1 resize-none"
                  data-testid="incident-description-input"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  className="flex-1 bg-violet-600 hover:bg-violet-500"
                  data-testid="save-incident-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Sauvegarder
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Incidents List */}
      {incidents.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="glass rounded-xl p-12 text-center"
        >
          <CheckCircle className="w-16 h-16 text-green-500/50 mx-auto mb-4" />
          <p className="text-gray-400">Aucun incident signalé</p>
        </motion.div>
      ) : (
        <div className="space-y-4">
          {incidents.map((incident, index) => {
            const statusInfo = getStatusInfo(incident.status);
            const severityInfo = getSeverityInfo(incident.severity);
            const StatusIcon = statusInfo.icon;
            
            return (
              <motion.div
                key={incident.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-xl p-5 group"
                data-testid={`incident-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${
                    incident.status === 'resolved' 
                      ? 'bg-green-500/20' 
                      : incident.status === 'ongoing'
                      ? 'bg-red-500/20'
                      : 'bg-yellow-500/20'
                  }`}>
                    <StatusIcon className={`w-5 h-5 ${statusInfo.color}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-white font-medium">{incident.title}</h3>
                      <span className={`px-2 py-0.5 rounded text-xs uppercase tracking-wider ${severityInfo.color}`}>
                        {severityInfo.label}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm mb-2">{incident.description}</p>
                    <div className="flex items-center gap-4 text-gray-500 text-xs">
                      <span>Créé le {new Date(incident.created_at).toLocaleString('fr-FR')}</span>
                      {incident.resolved_at && (
                        <span className="text-green-400">
                          Résolu le {new Date(incident.resolved_at).toLocaleString('fr-FR')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => openDialog(incident)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                      data-testid={`edit-incident-${index}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(incident.id)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      data-testid={`delete-incident-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminIncidents;
